import type { Location, PickupDeviation, Transport, TransportRequest } from '../../services/controlTowerApi';

export type TransportRow = {
    id: string;
    sourceId: string;
    kind: 'request' | 'confirmed';
    requestId: string;
    requestResolved: boolean;
    handlingUnitIds: string[];
    requestStatus?: TransportRequest['status'];
    orderNumber?: string;
    orderedAt?: string;
    pickupDate: string;
    pickupTime?: string;
    pickupStart?: string;
    pickupEnd?: string;
    requestedPickupDate?: string;
    requestedPickupTime?: string;
    pickupDeviation: PickupDeviation;
    pickupTimeZone: string;
    deliveryDate?: string;
    shipFrom: Location;
    shipTo: Location;
    unloadingPoint?: string;
    carrier?: string;
    plate?: string;
    loadType?: string;
    materials: string[];
    volume?: number;
    weight?: number;
    handlingUnitCount: number;
};

// The server determines which native groups match; it also compares pickup windows.
export const requestRow = (request: TransportRequest): TransportRow => ({
    id: `request:${request.id}`,
    sourceId: request.id,
    kind: 'request',
    requestId: request.id,
    requestResolved: true,
    handlingUnitIds: [],
    orderedAt: request.sent_at,
    requestStatus: request.status,
    pickupDate: request.requested_pickup.date,
    pickupTime: request.requested_pickup.time,
    requestedPickupDate: request.requested_pickup.date,
    requestedPickupTime: request.requested_pickup.time,
    pickupDeviation: 'confirmation_missing',
    pickupTimeZone: 'UTC',
    deliveryDate: request.requested_delivery?.date,
    shipFrom: request.ship_from,
    shipTo: request.ship_to,
    unloadingPoint: request.unloading_point_id,
    loadType: request.load_type,
    materials: [...new Set(request.materials.map(material => material.article_number))],
    volume: request.total_volume_m3,
    weight: request.total_gross_weight_kg,
    handlingUnitCount: request.handling_unit_count,
});

export const transportRows = (transport: Transport): TransportRow[] =>
    transport.handling_unit_groups
        .filter(group => group.matches)
        .map(group => ({
            id: `confirmed:${transport.id}:${group.transport_request_id}:${JSON.stringify(group.locations)}`,
            sourceId: transport.id,
            kind: 'confirmed',
            requestId: group.transport_request_id,
            requestResolved: !!group.request,
            handlingUnitIds: group.handling_units.map(unit => unit.id),
            orderNumber: transport.transport_number,
            orderedAt: group.request?.sent_at,
            pickupDate: transport.requested_pickup_date,
            pickupTime: transport.requested_pickup_time,
            pickupStart: transport.estimated_pickup_start,
            pickupEnd: transport.estimated_pickup_end,
            requestedPickupDate: group.request?.requested_pickup.date,
            requestedPickupTime: group.request?.requested_pickup.time,
            pickupDeviation: group.pickup_deviation,
            pickupTimeZone: group.pickup_time_zone,
            deliveryDate: group.request?.requested_delivery?.date,
            shipFrom: group.locations.ship_from,
            shipTo: group.locations.ship_to,
            unloadingPoint: group.locations.place_of_discharge,
            carrier: transport.carrier?.carrier_name,
            plate: transport.carrier?.truck_license_plate_number,
            loadType: group.request?.load_type,
            materials: [
                ...new Set(
                    group.handling_units.flatMap(unit =>
                        unit.loads.flatMap(load =>
                            load.type === 'specified' && load.article_number ? [load.article_number] : []
                        )
                    )
                ),
            ].sort(),
            handlingUnitCount: group.handling_units.length,
        }));

export const dateOnly = (value?: string, locale = 'en-GB') =>
    value
        ? new Intl.DateTimeFormat(locale, {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              timeZone: 'UTC',
          }).format(new Date(`${value.slice(0, 10)}T12:00:00Z`))
        : '—';
